// Contracts — kids cabinet APPLY (public booking). Day-slot lookup + family
// booking. The booking request is KIDS-CABINET-SPECIFIC (KidsBookReqSchema below)
// — it does NOT reuse the legacy platform KidsLeadReqSchema (../kids.ts), which
// stays single-`name` for the older apps/web kids-lead relay. Here each child is
// First+Last (latin) so the booking child name normalizes to the SAME "First Last"
// the moderator Session V1 uses → the packer matches participant ↔ shipped video
// on that join key. Enums are shared from ../kids (single source).
import { z } from 'zod';
import { KidsGenderSchema, KidsEthnicitySchema, KidsOfficeIdSchema } from '../kids';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
// Child name parts: latin letters + space/apostrophe/hyphen, must start with a
// letter. Mirrors NAME_RE in kids/session.ts so /apply and Session V1 produce the
// SAME normalized "First Last" (packer join key + R2 upload folder).
const NAME_RE = /^[A-Za-z][A-Za-z '\-]*$/;
// Booking phone: a leading + plus 7–12 digits. Our markets are US/CA (+1) and CIS
// (+7), 10 national digits → 11 total; 12 leaves a small buffer. The web normalizes
// raw input to this shape before submit (PhoneField strips to digits + re-adds +).
const BOOK_PHONE_RE = /^\+\d{7,12}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// GET /api/kids/apply/day-slots?office=&date=&count= → family start times.
export const KidsDaySlotsQuerySchema = z.object({
  office: z.string().trim().min(1).max(60),
  date: z.string().regex(DATE_RE),
  count: z.coerce.number().int().min(1).max(20).default(1),
});
export type KidsDaySlotsQuery = z.infer<typeof KidsDaySlotsQuerySchema>;

export const KidsDaySlotsResSchema = z.object({
  slots: z.array(z.object({ time: z.string().regex(TIME_RE) })),
});
export type KidsDaySlotsRes = z.infer<typeof KidsDaySlotsResSchema>;

// POST /api/kids/apply/book request body (kids-cabinet booking). Child = two
// latin-only parts; the worker normalizes them into the single `name` D1 column.
export const KidsBookChildSchema = z.object({
  firstName: z.string().trim().min(1).max(60).regex(NAME_RE),
  lastName: z.string().trim().min(1).max(60).regex(NAME_RE),
  age: z.number().int().min(5).max(17),
  gender: KidsGenderSchema,
  ethnicity: KidsEthnicitySchema,
});
export type KidsBookChild = z.infer<typeof KidsBookChildSchema>;

export const KidsBookReqSchema = z.object({
  parent: z.object({
    // First+Last (same latin shape as each child). The worker joins them into the
    // single `users.name` D1 column via normalizeName. The packer never matches the
    // parent (only each child), so the split is purely for form clarity / storage.
    firstName: z.string().trim().min(1).max(80).regex(NAME_RE),
    lastName: z.string().trim().min(1).max(80).regex(NAME_RE),
    phone: z.string().trim().regex(BOOK_PHONE_RE).max(30),
    email: z.string().trim().regex(EMAIL_RE).max(200),
  }),
  children: z.array(KidsBookChildSchema).min(1).max(10),
  meeting: z.object({
    office: KidsOfficeIdSchema,
    date: z.string().regex(DATE_RE),
    time: z.string().regex(TIME_RE),
  }),
  consents: z.object({ icf: z.literal(true), disclaimer: z.literal(true) }),
  // Email-verification OTP (B6): a 6-digit code the parent received via
  // POST /api/kids/apply/verify-request before submitting. REQUIRED — the
  // worker rejects the booking (400 bad_code) unless a matching, unexpired,
  // unused apply_otp row exists for parent.email + this code. Blocks the
  // bot-pool from accruing unverified bookings.
  code: z.string().regex(/^\d{6}$/),
  turnstileToken: z.string().min(1).max(4096).optional(),
  website: z.string().max(200).optional(), // honeypot
});
export type KidsBookReq = z.infer<typeof KidsBookReqSchema>;

// POST /api/kids/apply/verify-request — step 1 of B6 email verification. The
// parent supplies their email (already collected in step 1 of the wizard); the
// worker generates a 6-digit code, stores it in apply_otp (TTL ~10 min) and
// emails it. Enumeration-safe: always responds { ok: true } regardless of
// whether the email is new or known.
export const KidsApplyVerifyRequestReqSchema = z.object({
  email: z.string().trim().regex(EMAIL_RE).max(200),
});
export type KidsApplyVerifyRequestReq = z.infer<typeof KidsApplyVerifyRequestReqSchema>;

export const KidsApplyVerifyRequestResSchema = z.object({ ok: z.literal(true) });
export type KidsApplyVerifyRequestRes = z.infer<typeof KidsApplyVerifyRequestResSchema>;

// POST /api/kids/apply/book → result of a successful booking. Request body is
// KidsBookReqSchema (above). On a full window → 409 { error:'slot_unavailable' }.
export const KidsBookResSchema = z.object({
  ok: z.literal(true),
  meetingId: z.string(),
  assignments: z.array(z.object({ childId: z.string(), time: z.string() })),
  emailSent: z.boolean(),
});
export type KidsBookRes = z.infer<typeof KidsBookResSchema>;
